import {isFunction} from 'lodash';
import React, {forwardRef, ReactNode} from 'react';
import styled, {css} from 'styled-components';

import {
  buildDirectHoverSelector,
  buildHoverSelector,
} from '../../../../core/src/helpers/browser/hoverHelpers';
import {VisualSizesEnum} from '../../styles/commonStyles';
import {flexCenter} from '../../styles/mixins';
import {makeSizeConstants} from '../../styles/styleHelpers';
import {ThemeContextType, useUserTheme} from '../../theme/userThemeProvider';
import {Icon, IconName, IconProps} from '../icon/icon';
import {typographyMixin} from '../typography/typography';
import {ButtonInheritableProps, ButtonInheritableStyleProps} from './buttonChrome';
import {findButtonColors} from './buttonConstants';

/*
 * Props.
 */

export interface ButtonContentProps extends ButtonInheritableProps {
  className?: string;
  leftIcon?: IconName;
  leftIconColor?: IconProps['color'];
  rightIcon?: IconName;
  rightIconColor?: IconProps['color'];
  children?: ReactNode;
}

/*
 * Style.
 */

export const mainButtonHorizontalPadding = makeSizeConstants(7, 12, 16);

export const mainButtonContentStyles = makeSizeConstants(
  css`
    font-size: 13px;
    line-height: 16px;
    padding: 5px ${mainButtonHorizontalPadding[VisualSizesEnum.SMALL]}px 5px
      ${mainButtonHorizontalPadding[VisualSizesEnum.SMALL]}px;
  `,
  css`
    font-size: 14px;
    line-height: 17px;
    padding: 6px ${mainButtonHorizontalPadding[VisualSizesEnum.MEDIUM]}px 7px
      ${mainButtonHorizontalPadding[VisualSizesEnum.MEDIUM]}px;
  `,
  css`
    font-size: 16px;
    line-height: 20px;
    padding: 9px ${mainButtonHorizontalPadding[VisualSizesEnum.LARGE]}px;
    text-align: center;
  `,
);

export const roundedSmallButtonPaddingHorizontalPx = 12;
export const roundedSmallButtonPaddingVerticalPx = 5;

// This is intended to match the Figma spec for buttons:
// https://www.figma.com/file/qmN1rpf6Sza2iz7kXyQQGn/%E2%9C%A8-%F0%9F%96%A5-Buttons
export const roundedMainButtonContentStyles = makeSizeConstants(
  css`
    line-height: 16px;
    padding: ${roundedSmallButtonPaddingVerticalPx}px ${roundedSmallButtonPaddingHorizontalPx}px;
  `,
  css`
    line-height: 17px;
    padding: 6px 14px 7px 14px;
  `,
  css`
    line-height: 20px;
    padding: 9px 16px;
    text-align: center;
  `,
);

export const commonButtonStyles = css`
  ${typographyMixin({variant: 'body-bold'})}
`;

const StyledContentDiv = styled.div<ButtonInheritableStyleProps>`
  flex: 1;
  ${commonButtonStyles}

  ${(p) => (p.$isRounded ? roundedMainButtonContentStyles[p.$size] : mainButtonContentStyles[p.$size])};

  ${(p) => colorStyles(p)};
  background-color: ${(p) => {
    const buttonColors = findButtonColors(p.theme);
    return buttonColors[p.$variant].background(p.$color);
  }};

  transition: background-color 100ms ease;

  // Hover styles.
  ${(p) => {
    const colors = findButtonColors(p.theme);

    return css`
      ${p.$shouldUseDirectHover ? buildDirectHoverSelector() : buildHoverSelector()} {
        background-color: ${colors[p.$variant].backgroundHover(p.$color)};
      }
    `;
  }}

  // Pressed styles.
  [role='button']:active > &&,
  [role='button'][aria-expanded='true'] > && {
    background-color: ${(p) => {
      const buttonColors = findButtonColors(p.theme);
      return buttonColors[p.$variant].backgroundActive(p.$color);
    }};
  }

  // Disabled styles.
  [role='button'][aria-disabled='true'] > && {
    ${(p) => {
      const colors = findButtonColors(p.theme);

      const {textDisabled, backgroundDisabled} = colors[p.$variant];
      return css`
        ${textDisabled && `color: ${textDisabled};`}
        ${backgroundDisabled &&
        `background-color: ${isFunction(backgroundDisabled) ? backgroundDisabled(p.$color) : backgroundDisabled};`}
      ${p.$shouldDisallowCursorIfDisabled && `cursor: not-allowed`}
      `;
    }}
  }

  ${(p) =>
    (p.$hasRightIcon || p.$hasLeftIcon) &&
    css`
      ${flexCenter};
      gap: 4px;
    `}

  -webkit-app-region: no-drag;
`;

const StyledLeftIcon = styled(Icon)`
  margin-top: -2px;
  margin-bottom: -2px;
`;

const StyledRightIcon = styled(Icon)`
  margin-top: -2px;
  margin-bottom: -2px;
`;

function colorStyles(props: ButtonInheritableStyleProps & ThemeContextType) {
  const colors = findButtonColors(props.theme);
  const {text} = colors[props.$variant];

  if (isFunction(text)) {
    return css`
      color: ${text(props.$color)};
    `;
  }

  return css`
    color: ${text};
  `;
}

/*
 * Component.
 */

export const ButtonContent = forwardRef<HTMLDivElement, ButtonContentProps>(
  ({className, children, leftIcon, leftIconColor, rightIcon, rightIconColor, ...buttonProps}, ref) => {
    const theme = useUserTheme();
    const isRounded = buttonProps.isRounded ?? theme.rounded;

    return (
      <StyledContentDiv
        ref={ref}
        className={className}
        $size={buttonProps.size}
        $variant={buttonProps.variant}
        $isRounded={isRounded}
        $color={buttonProps.color}
        $isDisabled={buttonProps.isDisabled}
        $shouldDisallowCursorIfDisabled={buttonProps.shouldDisallowCursorIfDisabled}
        $shouldUseDirectHover={buttonProps.shouldUseDirectHover}
        $hasLeftIcon={Boolean(leftIcon)}
        $hasRightIcon={Boolean(rightIcon)}
      >
        {leftIcon && <StyledLeftIcon name={leftIcon} color={leftIconColor} />}
        {children}
        {rightIcon && <StyledRightIcon name={rightIcon} color={rightIconColor} />}
      </StyledContentDiv>
    );
  },
);

ButtonContent.displayName = 'ButtonContent';
